import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BreadCrumbs from "../../components/General/BreadCrumbs";
import SidebarBox from "../../components/General/SidebarBox";
import LoadingSkeleton from "../../components/UI/LoadingSkeleton";
import BrandImage from "../../components/Vehicles/BrandImage";
import FindVehicleFilter from "../../components/Vehicles/FindVehicleFilter";
import SingleNewVehicleClass from "../../components/Vehicles/SingleNewVehicleClass";
import SingleUsedVehicleCard from "../../components/Vehicles/SingleUsedVehicleCard";
import SortVehicles from "../../components/Vehicles/SortVehicles";
import { findVehicles } from "../../utils/fetcher";
import { VehicleConditions, VehicleNames } from "../../utils/GeneralTypes";
import { useScrollToTop } from "../../utils/generalUtils";
import style from "./index.module.css";
import axios from "axios";
import EmptyContent from "../../components/General/EmptyContent";
import { useTranslation } from "../../hooks/useTranslation";
import { useHistory, useLocation } from "react-router-dom";
import { selectUser } from "../../store/userSlice";
import { useSelector } from "react-redux";
import { updateWishlist } from "../VehiclePage/utils";
import { VehicleConditionsControllers } from "../../components/General/VehiclesControllers";
import Button from "../../components/UI/Button";

type SortCriteriaT = "lowest_price" | "highest_price" | "lowest_kilometers" | "highest_kilometers" | "newest_year";

function genSortCriteria(name: "price" | "mileage" | "year", direction: "asc" | "desc" | "none"): SortCriteriaT | null {
  if (direction === "none") {
    return null;
  }
  if (name === "price" && direction === "asc") {
    return "lowest_price";
  } else if (name === "price" && direction === "desc") {
    return "highest_price";
  } else if (name === "mileage" && direction === "asc") {
    return "lowest_kilometers";
  } else if (name === "mileage" && direction === "desc") {
    return "highest_kilometers";
  } else if (name === "year" && direction === "desc") {
    return "newest_year";
  }
  return "newest_year";
}

const FindVehicles = ({
  vehicleCondition,
  vehicleNames,
  isForDistributor: isForDistributorTemp,
}: ComponentPropsType) => {
  const { search: historySearch } = useLocation();
  const searchParams = useMemo(() => {
    return new URLSearchParams(historySearch);
  }, [historySearch]);
  const { locale } = useTranslation();
  const isForDistributor = isForDistributorTemp ?? false;
  const { user } = useSelector(selectUser);
  const history = useHistory();
  const initialMount = useRef<boolean>(true);
  const containerRef = useRef<HTMLDivElement>(null);
  /**
   * component global flag to decide if the data being loaded should be appended to component existing data
   * isLoadingExtra = true: user scrolled the list of data and caused loading more data so this data should be appended to existing data
   * isLoadingExtra = false: mostly means new filter so data should be dropped and new data should replace it
   */
  const isLoadingExtra = useRef<boolean>(false);
  const isSortDataChanging = useRef<boolean>(false);
  const cancelRequestControllers = useRef<AbortController[]>([]);

  useScrollToTop();

  const [componentData, setComponentData] = useState<{
    isFetching: boolean;
    data: {
      // TODO: add type to item instead of any
      vehicles: any[];
    };
    currentPage: number;
    isLastPage: boolean;
  }>({
    isFetching: true,
    data: {
      vehicles: [],
    },
    currentPage: 2,
    isLastPage: false,
  });

  const sortParam = searchParams.get("sort") as SortCriteriaT | null;

  const fetchFoundVehicles = useCallback(
    async (page: number) => {
      const abortController = new AbortController();
      cancelRequestControllers.current.push(abortController);
      const search = new URLSearchParams(searchParams);
      if (isForDistributor) {
        search.set("for_distributor", "1");
      }
      try {
        const res = await findVehicles(
          vehicleCondition,
          vehicleNames.single,
          `?${search.toString()}`,
          `number=30&page=${isSortDataChanging.current ? 1 : page}`,
          abortController.signal,
        );
        setComponentData((current) => ({
          isFetching: false,
          data: {
            vehicles: [
              ...(!isSortDataChanging.current
                ? !initialMount.current && page === 1
                  ? []
                  : current.data.vehicles
                : []),
              ...res.data.vehicles.data,
            ],
          },
          isLastPage: res.data.vehicles.meta.current_page === res.data.vehicles.meta.last_page,
          currentPage: initialMount.current
            ? current.currentPage
            : isSortDataChanging.current
            ? 2
            : current.currentPage + 1,
        }));
        isSortDataChanging.current = false;
      } catch (e) {
        if (!axios.isCancel(e)) {
          throw e;
        }
      }
    },
    [searchParams, isForDistributor, vehicleCondition, vehicleNames],
  );

  useEffect(() => {
    if (!initialMount.current) {
      setComponentData((current) => ({
        ...current,
        isFetching: true,
      }));
    }
    cancelRequestControllers.current.forEach((c) => {
      c.abort();
    });
    cancelRequestControllers.current = [];
    (async () => {
      await fetchFoundVehicles(1);
      initialMount.current = false;
    })();
    return () => {
      cancelRequestControllers.current.forEach((c) => {
        c.abort();
      });
      cancelRequestControllers.current = [];
    };
  }, [fetchFoundVehicles]);

  const onLoadMore = useCallback(async () => {
    if (
      componentData.data.vehicles.length > 0 &&
      !componentData.isLastPage &&
      !isLoadingExtra.current &&
      !componentData.isFetching
    ) {
      isLoadingExtra.current = true;
      componentData.isFetching = true;
      await fetchFoundVehicles(componentData.currentPage).then(() => {
        isLoadingExtra.current = false;
        componentData.isFetching = false;
      });
    }
  }, [componentData, fetchFoundVehicles]);

  const onToggleWishlistHandler = useCallback(
    async (vehicle: any) => {
      if (!user) {
        history.replace("/sign");
        return;
      }
      setComponentData((current) => ({
        ...current,
        data: {
          vehicles: current.data.vehicles.map((item: any) =>
            item.id === vehicle.id ? { ...item, wishlisted: !item.wishlisted } : item,
          ),
        },
      }));
      await updateWishlist(vehicleNames.single, vehicle.wishlisted ? "delete" : "add", vehicle.id.toString());
    },
    [history, user, vehicleNames.single],
  );

  return (
    <>
      {/* <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <BreadCrumbs
              pages={[
                { text: locale("home"), link: "/" },
                // {
                //   text: locale(vehicleNames.plural),
                //   link: `/${vehicleCondition}-${vehicleNames.plural}`,
                // },
                {
                  text: `${locale("find_a")} ${locale(vehicleNames.single)}`,
                  link: "",
                },
              ]}
            />
          </div>
        </div>
      </div> */}
      <div className="main_section" ref={containerRef}>
        <div className="container-lg">
          <div className="section_content_padding">
            <div className={`${style.page_row} row`}>
              <div className="col-lg-4">
                <SidebarBox>
                  <FindVehicleFilter
                    vehicleNames={vehicleNames}
                    vehicleCondition={vehicleCondition}
                    searchBaseUrl={isForDistributor ? `/distributor/${vehicleNames.plural}/find` : undefined}
                  />
                </SidebarBox>
              </div>
              <div className="col-lg-8">
                <div
                  className={`${style.all_vehicles}${
                    vehicleCondition !== "new" ? ` ${style.all_vehicles_used}` : ""
                  } row`}
                >
                  <div className={style.trucks_states}>
                    <VehicleConditionsControllers
                      active={vehicleCondition}
                      baseLink={`${vehicleNames.plural}/find`}
                      isCombinedLink={true}
                      extraParams={`?${searchParams.toString()}`}
                      isForListings={true}
                      vehicleNames={vehicleNames}
                    />
                  </div>
                  {!componentData.isFetching && componentData.data.vehicles.length === 0 ? (
                    <div className="col-12">
                      <EmptyContent>
                        <p>{locale("noResult")}</p>
                      </EmptyContent>
                    </div>
                  ) : null}
                  {((componentData.data.vehicles.length > 0 && !componentData.isFetching) ||
                    componentData.isFetching) &&
                    (componentData.isFetching ? (
                      <div className="col-12">
                        <LoadingSkeleton count={8} />
                      </div>
                    ) : (
                      <>
                        <div className="col-12" style={{ marginBottom: "30px" }}>
                          <SortVehicles
                            setSortData={(name: "price" | "mileage" | "year", value: "asc" | "desc") => {
                              const nextSortParam = genSortCriteria(name, value);
                              isSortDataChanging.current = true;
                              const nextSearchParams = new URLSearchParams(searchParams);
                              nextSearchParams.set("sort", nextSortParam ?? "");
                              history.push(
                                `/${vehicleCondition}-${vehicleNames.plural}/find${`?${nextSearchParams.toString()}`}`,
                              );
                            }}
                            groups={[
                              {
                                name: locale("price"),
                                buttons: [
                                  {
                                    active: sortParam === "lowest_price",
                                    name: ["price", "asc"],
                                  },
                                  {
                                    active: sortParam === "highest_price",
                                    name: ["price", "desc"],
                                  },
                                ],
                              },
                              ...(vehicleCondition === "new"
                                ? [
                                    {
                                      name: locale("year"),
                                      buttons: [
                                        {
                                          active: sortParam === "newest_year",
                                          name: ["year", "desc"],
                                        },
                                      ] as {
                                        name: [string, "asc" | "desc"];
                                        active: boolean;
                                      }[],
                                    },
                                  ]
                                : []),
                              ...(vehicleCondition !== "new"
                                ? [
                                    {
                                      name: locale("mileage"),
                                      buttons: [
                                        {
                                          active: sortParam === "lowest_kilometers",
                                          name: ["mileage", "asc"],
                                        },
                                        {
                                          active: sortParam === "highest_kilometers",
                                          name: ["mileage", "desc"],
                                        },
                                      ] as {
                                        name: [string, "asc" | "desc"];
                                        active: boolean;
                                      }[],
                                    },
                                  ]
                                : []),
                            ]}
                          />
                        </div>
                        {componentData.data.vehicles.map((item, index) =>
                          isForDistributor ? (
                            <div className={`${style.single_vehicle} col-12`} key={index}>
                              {index !== 0 && (
                                <div className={`${style.separator} section_content_padding`}>
                                  <div className="section_separator"></div>
                                </div>
                              )}
                              <BrandImage
                                image={item?.brand?.logo}
                                link={`/${vehicleCondition}-${vehicleNames.plural}/brands/${item?.brand?.id}`}
                                name={item?.brand?.name}
                                styleobject={{ marginBottom: 0 }}
                              />
                              <SingleNewVehicleClass
                                vehicleNames={vehicleNames}
                                classesNo={item.vehicles.length}
                                baseLink={`/distributor/${vehicleNames.plural}/${vehicleNames.single}`}
                                name={item.name}
                                price={item.vehicles[0].price.toString()}
                                image={item.image}
                                year={item?.year || item?.model?.year}
                                classes={item.vehicles.map((el: any) => ({
                                  id: el[`vehicle_id`],
                                  name: el.class_name,
                                  price: el.price.toString(),
                                }))}
                              />
                            </div>
                          ) : (
                            <div className="col-md-6" key={index}>
                              <SingleUsedVehicleCard
                                showroom={{
                                  is_showroom: item?.seller?.is_showroom,
                                  image: item?.showroom?.logo || item?.seller?.showroom?.logo,
                                  link: `/showrooms/showroom/${item?.showroom?.id || item?.seller.showroom?.id}`,
                                }}
                                isWishlist={item.wishlisted as boolean}
                                onToggleWishlist={() => onToggleWishlistHandler(item)}
                                image={item?.images[0]?.image}
                                link={`/${vehicleCondition}-${vehicleNames.plural}/${vehicleNames.single}/${item.id}`}
                                date={item?.created_at}
                                location={
                                  item?.seller?.region
                                    ? `${item.seller.region?.name}, ${item.seller.region?.city?.name}`
                                    : ""
                                }
                                name={`${item?.model?.brand?.name} ${item?.model?.name} ${
                                  item?.year || item?.model?.year
                                }`}
                                price={item?.price}
                                phone={item?.seller.is_showroom ? item.seller?.branch?.phone : item.seller?.user?.phone}
                                isFeatured={item?.listing_period.is_featured}
                              />
                            </div>
                          ),
                        )}
                        {!componentData.isLastPage && (
                          <div className="col-12 d-flex justify-content-center my-5">
                            <Button
                              buttonType="button"
                              styleType="primary"
                              disabled={componentData.isFetching}
                              isLoading={componentData.isFetching}
                              onClick={() => {
                                onLoadMore();
                              }}
                            >
                              {locale("load_more")}
                            </Button>
                          </div>
                        )}
                      </>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FindVehicles;

type ComponentPropsType = {
  vehicleNames: VehicleNames;
  vehicleCondition: VehicleConditions;
  isForDistributor?: boolean;
};
